/**
 * Files attached to an AppConfig row.
 *
 * The row itself holds only a reference — `{ title, path }` — and the bytes sit
 * in blob storage under `apps/<app>/<hospital>/<config>/<file>`, the same
 * hierarchy the row is keyed by. See docs/AppConfig-Framework.md and
 * apps/server/src/services/AppConfigFilesService.js.
 *
 * Uploads and deletes happen immediately, not on "שמור": the file is in storage
 * the moment it is picked, and what the admin saves is the list of references.
 * The alternative — holding File objects until save — loses every upload when a
 * validation error elsewhere on the form blocks the save.
 */
import API from '../config/axiosConfig';
import env from '../config/env';
import type { AcceptMap } from '../utils/file-validation';

/** One file referenced by a configuration row. */
export interface ConfigFileRef {
  /** What the user sees. Defaults to the uploaded file's name, then editable. */
  title: string;
  /** Blob path: apps/<app>/<hospital>/<config>/<file>. Absent for `builtin`. */
  path?: string;
  /**
   * A file that ships inside the app bundle rather than living in storage —
   * how an app offers a document out of the box, before any hospital has
   * uploaded anything. The app maps the key to its own imported asset; nothing
   * here can be uploaded to or deleted from storage, only removed from the list.
   */
  builtin?: string;
}

export interface ConfigFileUploadResult {
  path: string;
  baseUrl: string;
  originalName: string;
}

/**
 * What a picker offers, and the server's 20 MB cap.
 *
 * A deliberate subset of what appConfigFileRoutes.js accepts (DOCUMENT_EXTENSIONS
 * + IMAGE_EXTENSIONS, which also cover .txt/.csv/.eml/.msg/.gif/.bmp) — these are
 * the formats a department actually attaches to a configuration. Narrower is the
 * safe direction: it can never offer a format the server then refuses. If you
 * widen it, widen `createUploader` first.
 */
export const CONFIG_FILE_ACCEPT: AcceptMap = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};

export const CONFIG_FILE_MAX_BYTES = 20 * 1024 * 1024;

/**
 * A URL anyone can open — the apps that show these files serve anonymous
 * visitors, so the download route carries no token. Absolute, because in
 * development the client and the API are on different ports.
 */
export const configFileUrl = (path: string): string =>
  `${env.API_BASE_URL || ''}/api/config-files/${path}`;

export async function uploadConfigFile(
  appName: string,
  configName: string,
  hospital: string,
  file: File
): Promise<ConfigFileUploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  // handlingErrorLocally: the picker renders the failure inline next to the
  // file it belongs to; without this the same Hebrew line also appears in the
  // global floating error, twice over.
  const response = await API.post(
    `/api/config-files/${appName}/${configName}/${encodeURIComponent(hospital)}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, handlingErrorLocally: true }
  );
  return response.data?.data;
}

export async function deleteConfigFile(
  appName: string,
  configName: string,
  hospital: string,
  path: string
): Promise<void> {
  await API.delete(
    `/api/config-files/${appName}/${configName}/${encodeURIComponent(hospital)}`,
    { params: { path }, handlingErrorLocally: true }
  );
}
